import { queryKeys } from "@/client";
import { type DirectorySubmissionFull } from "@/components/DirectorySubmissions/Form/types";
import { useAuth } from "@/contexts/AuthHook";
import {
  type RealtimeChannel,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

interface UseRealtimeFormUpdatesParams {
  supabase: SupabaseClient | null;
  submissionId: string | undefined;
  isLoadedSuccessfully: boolean;
}

const tableFormKeyMapping: Record<string, string> = {
  A4: "directory_submission_contact_persons",
  B4: "directory_submission_department_heads",
  B5: "directory_submission_partner_changes",
  B6: "directory_submission_lawyer_info",
  C1: "directory_submission_barristers_used",
  PC: "directory_submission_publishable_clients",
  CC: "directory_submission_confidential_clients",
};

const tableToClientProp: Record<string, keyof DirectorySubmissionFull> = {
  directory_submission_contact_persons: "contactPersons",
  directory_submission_department_heads: "departmentHeads",
  directory_submission_partner_changes: "partnerChanges",
  directory_submission_lawyer_info: "lawyerInfo",
  directory_submission_barristers_used: "barristersUsed",
  directory_submission_publishable_clients: "publishableClients",
  directory_submission_confidential_clients: "confidentialClients",
};

const formKeyToClientProp = (formKey: keyof typeof tableFormKeyMapping) =>
  tableToClientProp[tableFormKeyMapping[formKey]];

export function useRealtimeFormUpdates({
  supabase,
  submissionId,
  isLoadedSuccessfully,
}: UseRealtimeFormUpdatesParams) {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const subscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isLoadedSuccessfully || !submissionId || !supabase) {
      return;
    }

    let currentChannel: RealtimeChannel | null = null;

    const subscribe = () => {
      if (currentChannel) {
        supabase.removeChannel(currentChannel).catch(() => {});
      }

      const updateQueryData = <T extends DirectorySubmissionFull>(
        updater: (oldData: T) => T,
      ) => {
        queryClient.setQueryData<T>(
          queryKeys.directorySubmission(String(submissionId)),
          (oldData: T | undefined) => {
            if (!oldData) return oldData as any;
            return updater(oldData);
          },
        );
      };

      const handleOverviewUpdate = (payload: any) => {
        const updated = payload.new;
        if (updated?.updated_by && updated.updated_by === userId) return;

        updateQueryData((old) => ({
          ...old,
          firm_name: updated.firm_name ?? old.firm_name,
          department_name: updated.department_name ?? old.department_name,
          partners_count: updated.partners_count ?? old.partners_count,
          partners_male_female_ratio:
            updated.partners_male_female_ratio ??
            old.partners_male_female_ratio,
          other_lawyers_count:
            updated.other_lawyers_count ?? old.other_lawyers_count,
          other_lawyers_male_female_ratio:
            updated.other_lawyers_male_female_ratio ??
            old.other_lawyers_male_female_ratio,
          ethnic_minority_percentage:
            updated.ethnic_minority_percentage ??
            old.ethnic_minority_percentage,
          lgbt_percentage: updated.lgbt_percentage ?? old.lgbt_percentage,
          disability_percentage:
            updated.disability_percentage ?? old.disability_percentage,
          department_specialties:
            updated.department_specialties ?? old.department_specialties,
          coverage_feedback: updated.coverage_feedback ?? old.coverage_feedback,
          directory_submission_overview_id:
            updated.id ?? old.directory_submission_overview_id,
        }));
      };

      const handleTableUpdate = (
        payload: any,
        formKey: keyof typeof tableFormKeyMapping,
      ) => {
        const updatedRow = payload.new;
        if (updatedRow?.updated_by && updatedRow.updated_by === userId) return;

        const prop = formKeyToClientProp(formKey);
        if (!prop) return;

        updateQueryData((old) => {
          const arr = Array.isArray(old[prop]) ? (old[prop] as any[]) : [];
          const next = arr.map((row) =>
            row.id === updatedRow.id ? { ...row, ...updatedRow } : row,
          );
          return { ...old, [prop]: next };
        });
      };

      const handleTableInsert = (
        payload: any,
        formKey: keyof typeof tableFormKeyMapping,
      ) => {
        const newRow = payload.new;
        if (newRow?.updated_by && newRow.updated_by === userId) return;

        const prop = formKeyToClientProp(formKey);
        if (!prop) return;

        updateQueryData((old) => {
          const arr = Array.isArray(old[prop]) ? (old[prop] as any[]) : [];
          if (arr.some((r) => r.id === newRow.id)) return old; // prevent dupes
          const next = [...arr, newRow].sort(
            (a, b) => (a.row_order ?? 0) - (b.row_order ?? 0),
          );
          return { ...old, [prop]: next };
        });
      };

      const handleTableDelete = (
        payload: any,
        formKey: keyof typeof tableFormKeyMapping,
      ) => {
        const id = payload.old?.id;
        if (id == null) return;

        const idNum = Number(id); // normalize

        updateQueryData((old) => {
          const prop = formKeyToClientProp(formKey);
          if (!prop) return old;

          const arr = Array.isArray((old as any)[prop])
            ? ((old as any)[prop] as Array<{ id: number | string }>)
            : [];
          if (!arr.some((r) => Number(r.id) === idNum)) return old; // guard

          return { ...old, [prop]: arr.filter((r) => Number(r.id) !== idNum) };
        });
      };

      const handleMatterUpdate = (payload: any) => {
        const updatedMatter = payload.new;
        if (updatedMatter?.updated_by && updatedMatter.updated_by === userId)
          return;

        updateQueryData((old) => {
          const arr = Array.isArray(old.matters) ? old.matters : [];
          const next = arr.map((m) =>
            m.id === updatedMatter.id ? { ...m, ...updatedMatter } : m,
          );
          return { ...old, matters: next };
        });
      };

      const handleMatterDelete = (payload: any) => {
        const id = payload.old?.id;
        if (id == null) return;
        const idNum = Number(id);

        updateQueryData((old) => {
          const arr = Array.isArray(old.matters) ? old.matters : [];
          if (!arr.some((m) => Number(m.id) === idNum)) return old;
          return { ...old, matters: arr.filter((m) => Number(m.id) !== idNum) };
        });
      };

      currentChannel = supabase
        .channel(`db-changes-for-submission-${submissionId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "directory_submission_overview",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          handleOverviewUpdate,
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_contact_persons",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "A4");
            if (p.eventType === "INSERT") handleTableInsert(p, "A4");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_contact_persons",
          },
          (p) => {
            handleTableDelete(p, "A4");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_department_heads",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "B4");
            if (p.eventType === "INSERT") handleTableInsert(p, "B4");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_department_heads",
          },
          (p) => {
            handleTableDelete(p, "B4");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_partner_changes",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "B5");
            if (p.eventType === "INSERT") handleTableInsert(p, "B5");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_partner_changes",
          },
          (p) => {
            handleTableDelete(p, "B5");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_lawyer_info",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "B6");
            if (p.eventType === "INSERT") handleTableInsert(p, "B6");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_lawyer_info",
          },
          (p) => {
            handleTableDelete(p, "B6");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_barristers_used",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "C1");
            if (p.eventType === "INSERT") handleTableInsert(p, "C1");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_barristers_used",
          },
          (p) => {
            handleTableDelete(p, "C1");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_publishable_clients",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "PC");
            if (p.eventType === "INSERT") handleTableInsert(p, "PC");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_publishable_clients",
          },
          (p) => {
            handleTableDelete(p, "PC");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "directory_submission_confidential_clients",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          (p) => {
            if (p.eventType === "UPDATE") handleTableUpdate(p, "CC");
            if (p.eventType === "INSERT") handleTableInsert(p, "CC");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_submission_confidential_clients",
          },
          (p) => {
            handleTableDelete(p, "CC");
          },
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "directory_matter",
            filter: `directory_submission_id=eq.${submissionId}`,
          },
          handleMatterUpdate,
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "directory_matter",
          },
          handleMatterDelete,
        )
        .subscribe();
    };

    subscribeRef.current = subscribe;

    subscribe();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        subscribeRef.current?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (currentChannel) {
        supabase.removeChannel(currentChannel).catch(() => {});
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoadedSuccessfully, submissionId, supabase, userId, queryClient]);
}
